import { Component, OnInit, AfterViewInit, OnDestroy, Input, ChangeDetectionStrategy, ChangeDetectorRef, ViewRef, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import * as fromClassificationSearch from 'src/app/store/feature-stores/classification-search-store';
import { UntypedFormGroup } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { AppInsightsService } from 'src/app/core/appinsights.service';
import { CommonFunctions } from 'src/app/shared/common/common-functions';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { environment } from 'src/environments/environment';
import { RegApiService } from 'src/app/services/reg-api.service';
import { ClassificationSearchViewCoreFunctions } from '../classification-search-view-core/classification-search-view-core.functions';


@Component({
  selector: 'app-classification-search-view-core',
  templateUrl: './classification-search-view-core.component.html',
  styleUrls: ['./classification-search-view-core.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ClassificationSearchViewCoreComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() logPrefix?: string;
  @Input() routerUrl?: string;
  @Input() formGroup?: UntypedFormGroup;
  @Input() propertyId?: string;
  @Input() showClassDescription: boolean = false;
  @Input() showDownloadPdf: boolean = false;
  @Output() cuChanged = new EventEmitter<any>(); 
  
  subscriptions: Subscription[] = [];  

  internetRegistration: any;
  continueButtonHoverText: string;  

  foundClassification: string;
  
  worksafebcbcUrl = environment.worksafebcbcUrl;
  totalPercentageEqual100 = 'The total percentage for revenue must add up to 100%.';
  likeWorkSafeBCTtoAssignMeClassificationUnit = 'I\'d like WorkSafeBC to assign me a classification unit.';   
  tooltip= 'If you feel that your business may qualify for more than one classification unit, please choose to have our team review your application.';
  loadingFromApi = false;


  constructor(
    private store$: Store<fromClassificationSearch.State.State>,
    private appInsightService: AppInsightsService,
    private commonFunctions: CommonFunctions,
    private cd: ChangeDetectorRef,
    private classificationSearchViewCoreFunctions: ClassificationSearchViewCoreFunctions,
    private sessionStorageService: SessionStorageService,
    private regApiService: RegApiService) { }

  ngOnInit() {   
    this.setClassificationUnits([]);
    this.foundClassification = null;
    if ( this.formGroup && this.formGroup.controls.searchButtonClicked) {
      this.formGroup.controls.searchButtonClicked.setValue('N');
    }

    this.subscriptions.push(
      this.store$.pipe(select(fromClassificationSearch.Selectors.getInternetRegistration)).subscribe(
        internetRegistration => {         
          this.internetRegistration = internetRegistration;
          if (internetRegistration) {           
            this.classificationSearchViewCoreFunctions.getBusinessData(this.formGroup, internetRegistration);
          }         
        }
      )
    );
   }

  ngAfterViewInit() { }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  searchCU() {       
    try {      
      this.appInsightService.logEvent(this.routerUrl + ' - searchCU button clicked');
      this.setClassificationUnits([]);   
      const iRegReferenceNumber = this.internetRegistration?.registrationSession?.iRegReferenceNumber; // for trace
      const searchByML = this.commonFunctions.searchByML(this.internetRegistration, this.logPrefix, this.sessionStorageService);
      const payload = this.classificationSearchViewCoreFunctions.getSearchPayload(this.formGroup, iRegReferenceNumber, searchByML);
      
      const mergedClassificationBySearchCriteriaPayload = {
        businessDescription: payload.payload.businessDescription,
        productsServices: payload.payload.productsServices,
        irRefNumber: payload.payload.irRefNumber,
        searchByML: payload.payload.searchByML
      };

      this.formGroup.controls.searchButtonClicked.setValue('Y'); 
      this.getClassificationFromApi(mergedClassificationBySearchCriteriaPayload);  

    } catch (error) { 
      console.log('searchCU: error=' + error);
    }     
  }

  disableSearch() {    
    return this.classificationSearchViewCoreFunctions.disableSearch(this.formGroup);
  }


  addNewProductDetails(){
    this.classificationSearchViewCoreFunctions.addNewProductDetails(this.formGroup);
  }
  
  removeProductDetails(index: number){     
    this.classificationSearchViewCoreFunctions.removeProductDetails(index, this.formGroup);     
  }

  

  private detectChanges() {
    // Programmatically run change detection to fix issue in Safari
    setTimeout(() => {
      console.log('[Classification Search] detectChanges');
      try {
        if ( this.cd !== null &&
            this.cd !== undefined &&
            ! (this.cd as ViewRef).destroyed ) {
                this.cd.detectChanges();
        }
      } catch (ex) {
        const error = ex;
        console.log('[Classification Search] detectChanges ex=' + ex);
      }
    }, 250);
  }

  private getClassificationFromApi(mergedClassificationBySearchCriteriaPayload: any) {
    this.loadingFromApi = true; 
    this.regApiService.getClassificationBySearchCriteria(mergedClassificationBySearchCriteriaPayload).subscribe({
        next: (response) => {
          console.log('[Classification Search] getClassificationFromApi response=' + JSON.stringify(response));
          this.loadingFromApi = false;
          if (response != null) { 
            this.reloadCu(response.classificationUnitSearchResults, this.internetRegistration); 
          } 
        },
        error: (err) => {
          this.loadingFromApi = false;
          this.cd.detectChanges();
          console.log('[Classification Search] getClassificationFromApi error');
        },
        complete: () => {
          this.loadingFromApi = false;
          this.cd.detectChanges();
          console.log('[Classification Search] getClassificationFromApi completed');
        }         
    });    
  }

  private reloadCu(classificationUnitSearchResults: any, internetRegistration: any) {
    const name = 'reloadCu';
    const classifications = classificationUnitSearchResults;
    if (classifications && classifications.length > 0) {
      this.setClassificationUnits([]);
      console.log(name + '[Classification Search] getClassifications count=: ' + classifications.length);           
      classifications.forEach((item: {
        classificationUnitCode: number;
        classId: number;
        className: string;
        rate: number;
        score: number;
        mlSearch: string;
        classDescription: string;
        }) => {
        const classification = {
          classificationUnitCode: item.classificationUnitCode,
          classId: item.classId,
          name: item.className,
          description: `${item.className}${this.commonFunctions.formatClassificationUnitName(item.classificationUnitCode)}`,
          rate: item.rate,
          score: item.score,
          mlSearch: item.mlSearch,
          classDescription: item.classDescription
        };            
        this.classificationSearchViewCoreFunctions.addNewClassificationUnit(classification);
      });
      const class99 = {
        classificationUnitCode: -1,
        classId: -99,
        name: this.likeWorkSafeBCTtoAssignMeClassificationUnit,
        description: this.likeWorkSafeBCTtoAssignMeClassificationUnit };
      this.classificationSearchViewCoreFunctions.addNewClassificationUnit(class99);
      
      console.log(name + '[Classification Search] Loaded Classification units: ');
      this.updateSelectedIdWhenDataFound(internetRegistration);
      this.foundClassification = 'Y';
    } else { 
      this.formGroup.controls.classificationUnit.reset();
      this.formGroup.controls.classificationUnitScore?.reset();                        
      console.log(name + '[Classification Search] noMatchingClassificationUnitsFound');
      this.updateSelectedIdWhenNoDataFound();   
      if (this.formGroup.controls.searchButtonClicked?.value === 'Y') {
        this.foundClassification = 'N';          
      }            
    }

    if (this.classificationSearchViewCoreFunctions.isSearchButtonClicked(this.formGroup)) {
      this.cuChanged.emit(this.formGroup.controls.classificationUnit.value);
    }          

    this.detectChanges();
  }  


  updateSelectedIdWhenDataFound(internetRegistration: any) {
    let classId: string;
    if (internetRegistration.classification != null &&
        internetRegistration.classification.classificationSession.slice(-1)[0]) {
      const classificationSession = internetRegistration.classification.classificationSession.slice(-1)[0];
   
      if (classificationSession && classificationSession.classificationAssignment != null) {      
        if (classificationSession.classificationAssignment.length > 0 &&
            classificationSession.classificationAssignment.slice(-1)[0].classificationId) {
            classId = classificationSession.classificationAssignment[0].classificationId;
            classId = classId !== null ? classId.toString().trim() : classId;
        }

        // isNoneCUApplyFlag is not reliable.
        // If None of the above apply selected.
        // const sessionNumber = parseInt(classificationSession.sessionNumber);
        // if (!classId && classificationSession.classificationAssignment.length === 0 && sessionNumber > 1) {
        //   classId = '-99';
        // }
       
        this.formGroup.controls.classificationUnit.reset();
        this.formGroup.controls.classificationUnitScore?.reset();

        const classificationUnits = this.getClassificationUnits();
        if (classId && classificationUnits) {
          const classificationsFound = classificationUnits.find(p => p.classId.toString() === classId);
          if (classificationsFound) {
            let score = this.getScoreByClassId(classId);          
            this.formGroup.controls.classificationUnit.setValue(classId);
            this.classificationSearchViewCoreFunctions.setFormValue(this.formGroup, 'classificationUnitScore', score, 'updateSelectedIdWhenDataFound');
          }          
        }
      }
    }
  }

  updateSelectedIdWhenNoDataFound() {
    let classId = '-99';
    let score = this.getScoreByClassId(classId);
    this.formGroup.controls.classificationUnit.setValue(classId);    
    this.classificationSearchViewCoreFunctions.setFormValue(this.formGroup, 'classificationUnitScore', score, 'updateSelectedIdWhenNoDataFound');
    console.log('[Classification Search] updateSelectedIdWhenNoDataFound - no data found by search key: classId=' + classId+ ' score=' + score);
  }  

  public getClassificationUnits() {
    return this.classificationSearchViewCoreFunctions.getClassificationUnitsData();
  }

  public setClassificationUnits(value: any) {
    this.classificationSearchViewCoreFunctions.setClassificationUnitsData(value); 
  }

  private unsubscribeAll() {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
    this.classificationSearchViewCoreFunctions.unsubscribeAll();
  } 

  errorMessage() {
    if (this.formGroup.controls.searchKey &&
      this.formGroup.controls.searchKey.value.length === 0) {
     return 'Please enter a keyword to search.';
    } else if (this.formGroup.controls.searchButtonClicked &&
      this.formGroup.controls.searchButtonClicked.value !== 'Y') {
     return this.classificationSearchViewCoreFunctions.clickSearchButtonToProceedErrorMessage;
    } else {
     return '';
   }
  }


  getScoreByClassId(classId: string) {
    let score = 0;
    try {
      if (classId != null && classId != '-99' ) {
        const cuId = parseInt(classId);
        const classificationUnits = this.getClassificationUnits();
        const find =  classificationUnits.find(x=>x.classId == cuId);
        score = find.score;
      }
    }
    catch(ex) {
      console.log('[Classification Search] getScoreByClassId ex=' + ex);
    }
    
    return score.toString();
  }

  saveScore(classId: string) {
    console.log('[Classification Search] saveScore classId=' + classId);          
    this.classificationSearchViewCoreFunctions.setFormValue(this.formGroup, 'classificationUnitScore', this.getScoreByClassId(classId), 'saveScore');
    this.cuChanged.emit(classId);
  } 

  isSearchButtonClicked() {
    return this.classificationSearchViewCoreFunctions.isSearchButtonClicked(this.formGroup);
  }

  isInvalidAndTouched(formGroup: UntypedFormGroup, propertyName: string): any {
    return formGroup.get(propertyName) != null &&
    ( this.formGroup.get(propertyName).touched || this.formGroup.get(propertyName).dirty) &&
    this.formGroup.get(propertyName).invalid;
  }  
  
  onHasMultipleRevenueStreamsChange(value: string) { 
    this.formGroup.controls.searchButtonClicked?.setValue(null); 
    this.classificationSearchViewCoreFunctions.onHasMultipleRevenueStreamsChange(value, this.formGroup);
  } 
}
